// The Add section of the launcher (the Launcher applet draws it, from a spec, #add while open): Webtop and
// a Shortcut tile first, then every widget kind as a tile (its icon and name, its `about` line as the tooltip). Picking a widget adds it to the webtop with its top-left corner where the menu
// was opened (the plus in the webtop menu's quick bar) or the webtop was double-clicked, or in the middle of the
// screen (the Add button in the notch, the Add entry of the launcher); picking Shortcut adds one there, the tile
// centred on that spot. It replaced the Add Widget submenu, which had grown too long, and later the webtop menu's
// New Webtop, Add Shortcut and Add Widget rows (#102). Webtop and
// Shortcut stay first (a webtop is mostly shortcuts); the widgets follow by
// name, A to Z, whatever the registry's order in widgets/index.js (which still decides the order of the widget
// stylesheets). The Webtops section pins New Webtop first too.
// It opens for everyone, since anyone may start a webtop: what it lists depends on the role on this webtop, so a
// viewer (or a locked webtop) sees Webtop alone, and an editor sees everything.
import { WIDGETS } from '../widgets/index.js';
import { lineIcon } from '../widgets/kit.js';
import { appletService, appletWidgetEntries, LAUNCHER, serviceNow, webtops, withService } from './applets.js';
import { pageX, pageY, screenH, screenW, scrolled, snap, TILE } from './geometry.js';
import { iconGlyph } from './overlay.js';
import { addShortcutAt, addWidgetAt } from './shortcuts.js';
import { currentRole, $webtop, webtop } from './state.js';
import { toast } from './ui.js';

// The Add button in the notch: opens this section. Always shown, for viewers too (Webtop).
export const $addBtn = document.getElementById('add-btn');
const SECTION = 'add';

// Where a picked tile goes, in unscaled px on the (scrolled) webtop: set each time the section opens.
let at = null;

// What the overlay lists, in order: `{label, about, icon, match, add(spot), pinned?, verb?}` (pinned: always first, its
// mark in the brand yellow; skip: Return passes over it unless it is the only match, as the launcher does with New
// Webtop; verb: the tooltip's first word, 'Add' by default). Built on each call, not at load, so it
// reads nothing from other modules while they load. Only editors can add tiles, so a viewer gets Webtop alone.
function entries() {
  const byName = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  const all = [
    {
      label: 'Webtop',
      verb: 'Create',
      pinned: true,
      skip: true,
      about: 'A new, empty webtop of its own',
      icon: lineIcon('webtops'),
      match: 'new webtop\npage\ndesktop',
      add: () => webtops((w) => w.newWebtop()),
    },
    {
      label: 'Shortcut',
      pinned: true,
      about: 'A link to a site or to another webtop',
      icon: lineIcon('shortcut'),
      match: 'shortcut\nlink\nbookmark\nurl',
      add: ({ x, y }) => addShortcutAt(snap(x - TILE.w / 2, y - TILE.h / 2)),
    },
    // the page's widgets (a kind marked `hidden` is listed through what registers it), then the widgets active
    // applets registered (client/lib/applets.js), each added as an `applet` tile naming its applet and widget
    ...[
      ...Object.entries(WIDGETS).filter(([, w]) => !w.hidden).map(([kind, w]) => ({ ...w, match: kind, add: (spot) => addWidgetAt(kind, spot) })),
      ...appletWidgetEntries().map((w) => ({ ...w, add: (spot) => addWidgetAt(w.kind, spot, { size: w.size, ...w.fields }) })),
    ].sort(byName),
  ];
  return currentRole() === 'editor' ? all : all.filter((it) => it.label === 'Webtop');
}
// An entry as a launcher item; the launcher closes before `action` runs, so the spot is read first.
function entryItem(it) {
  return {
    text: `${it.label}\n${it.about}\n${it.match}`, label: it.label, title: `${it.verb ?? 'Add'} ${it.label}: ${it.about}`,
    cls: `cmd${it.pinned ? ' pinned' : ''}`, skip: it.skip,
    glyph: () => iconGlyph(it.icon), // markup from this module or the widget's own, never user text
    action: () => it.add(at),
  };
}
const addSpec = () => ({
  id: SECTION, section: SECTION, domId: 'add', button: '#add-btn', title: 'Add', label: 'Add',
  placeholder: 'Filter…', empty: 'Nothing matches.',
  columns: () => [{ items: entries().map(entryItem) }],
});

// Opens the section for a widget whose top-left corner goes at `spot` ({x, y}, unscaled px on the webtop; centred on
// the screen when left out), or closes it. Anyone may open it: entries() leaves a viewer only Webtop.
export function toggleAdd(open = serviceNow(LAUNCHER)?.current() !== SECTION, spot = null) {
  if (!open) {
    const l = serviceNow(LAUNCHER);
    if (l?.current() === SECTION) l.close();
    return;
  }
  // Taken before it opens; scrolled() keeps the webtop's scroll while it is open anyway.
  at = spot ?? { x: screenW() / 2, y: scrolled() + screenH() / 2, centre: true };
  appletService(LAUNCHER).then((l) => l.open(addSpec())).catch(toast);
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  // its entry on the launcher's rail, for editors, matched by the widgets' names too
  withService(LAUNCHER, (l) => l.addEntry({
    id: SECTION, label: 'Add', icon: 'plus', order: 30, when: () => !!webtop?.shortcuts && currentRole() === 'editor',
    text: `Add\nwidgets\n${Object.values(WIDGETS).map((w) => w.label).join('\n')}`,
    action: () => toggleAdd(true),
  }));
  $addBtn.addEventListener('click', () => toggleAdd());
  // A double click on the webtop itself (not on a tile) opens it there, for editors only: a viewer has nothing to
  // add at a spot, and Webtop stays a deliberate click on a plus.
  $webtop.addEventListener('dblclick', (e) => {
    if (e.target.closest('.tile') || currentRole() !== 'editor') return;
    e.preventDefault();
    toggleAdd(true, { x: pageX(e.clientX), y: pageY(e.clientY) });
  });
}
